/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. 
 */

import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import axios from 'axios';
import React, {useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { PageLayout } from './components/PageLayout';
import { AgentDisplay } from './components/AgentDisplay';
import { Home } from './components/Home';
import { Routes, Route } from "react-router-dom";
import { AgentEdit } from './components/AgentEdit';

import './styles/App.css';

const MainContent = () => {
    /**
     * useMsal is a hook that returns the PublicClientApplication instance.
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
     */
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    // Check if user is authenticated and retrieve user's role
    const isAuthenticated = useIsAuthenticated();
    const userRoles = useMemo(() => {
        return isAuthenticated && activeAccount && activeAccount.idTokenClaims && activeAccount.idTokenClaims.roles ? activeAccount.idTokenClaims.roles : [];
      }, [isAuthenticated, activeAccount]);

    // Check if user is admin
    const isAdmin = userRoles && userRoles.includes("kmicro-fam.admin")?true:false;
    // Package that up as userProps for passing to other pages
    const userProps = {
        isAuthenticated,
        userRoles,
        isAdmin
      };

    // Add interceptor to inject bearer token in all API calls when user is authenticated
    if (isAuthenticated) {
        // const interceptor = axios.interceptors.request.use(async (config) => {
        axios.interceptors.request.use(async (config) => {
            try {
                const response = await instance.acquireTokenSilent({
                    //scopes: ["api://b2ab0c8d-b404-4149-84b6-8c914ea957e9/Test.Read"],
                    scopes: ["api://"+activeAccount.idTokenClaims.aud+"/Get.BearerToken"] ,
                    account: activeAccount
                });

                config.headers['x-kmicro-fam-authorization'] = `Bearer ${response.accessToken}`;
                return config;
            } catch (error) {
                console.error('Error adding Authorization header:', error);
                return Promise.reject(error);
            }
        });
    }


    /**
     * Most applications will need to conditionally render certain components based on whether a user is signed in or not.
     * msal-react provides 2 easy ways to do this. AuthenticatedTemplate and UnauthenticatedTemplate components will
     * only render their children if a user is authenticated or unauthenticated, respectively. For more, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
     */
    return (
        <div className="App">
            <Container>
                <Routes>
                    <Route path="/" element={<Home userProps={userProps}/>} />

                    {isAuthenticated ? (
                            <>
                                <Route path="/agent/*" element={<AgentDisplay clientApplication={instance} />} />
                            </>
                        ) : (
                            <>
                                <Route path="/agent" element={<Home />} />
                            </>
                        )
                    }
                    {isAdmin ? (
                            <>
                                <Route path="/agentedit/*" element={<AgentEdit clientApplication={instance}/>} />
                                <Route path="/agentedit" element={<AgentEdit clientApplication={instance}/>} />
                            </>
                        ) : (
                            <Route path="/admin" element={<Home />} />
                        )
                    }
                </Routes>
            </Container>
        </div>
    );
};

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const App = ({ instance, msalConfig }) => {
    return (
        <MsalProvider instance={instance}>
            <PageLayout msalConfig={msalConfig}>
                <MainContent />
            </PageLayout>
        </MsalProvider>
    );
};

export default App;
